import type { Application } from "@/entities/application/model/types";
import { centsToDollars, dollarsToCents } from "@/shared/lib/format";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  mapApplicationShortToUI,
  mapApplicationToUI,
  mapApplicationToUpdate,
} from "../application-mappers";
import {
  ApplicationConfigUpdateSchema,
  ApplicationCreateSchema,
  ApplicationOfferConfigInputSchema,
  ApplicationOutputSchema,
  applicationsApi,
  GetApplicationsParams,
} from "../applications";

export const applicationsKeys = {
  all: ["applications"] as const,
  lists: () => [...applicationsKeys.all, "list"] as const,
  list: (filters?: GetApplicationsParams) => [...applicationsKeys.lists(), filters] as const,
  details: () => [...applicationsKeys.all, "detail"] as const,
  detail: (id: string) => [...applicationsKeys.details(), id] as const,
};

export function useApplications(params?: GetApplicationsParams) {
  return useQuery({
    queryKey: applicationsKeys.list(params),
    queryFn: async () => {
      const response = await applicationsApi.getApplications(params);
      return {
        items: response.items.map(mapApplicationShortToUI),
        total: response.total,
        offset: response.offset,
        limit: response.limit,
      };
    },
  });
}

export function useApplication(applicationId: string) {
  return useQuery({
    queryKey: applicationsKeys.detail(applicationId),
    queryFn: async () => applicationsApi.getApplication(applicationId),
    enabled: !!applicationId,
  });
}

export function useApplicationOffers(
  applicationId: string | undefined,
  amount: number | undefined,
  downPayment: number | undefined,
  enabled: boolean = false
) {
  return useQuery({
    queryKey: [...applicationsKeys.detail(applicationId || ""), "offers", amount, downPayment],
    queryFn: async () => {
      if (!applicationId || amount === undefined || downPayment === undefined) {
        throw new Error("Application ID, amount, and down payment are required");
      }
      const response = await applicationsApi.getApplicationOffers(applicationId, {
        amount: dollarsToCents(amount),
        down_payment: dollarsToCents(downPayment),
      });
      return response.map((offer) => ({
        ...offer,
        amount: centsToDollars(offer.amount),
        downPayment: centsToDollars(offer.downPayment),
        monthlyPayment: centsToDollars(offer.monthlyPayment),
        promoMonthlyPayment: centsToDollars(offer.promoMonthlyPayment),
        financedAmount: centsToDollars(offer.financedAmount),
      }));
    },
    enabled: enabled && !!applicationId && amount !== undefined && downPayment !== undefined,
  });
}

export function useCreateApplication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ApplicationCreateSchema) => {
      const apiData = await applicationsApi.createApplication(data);
      return mapApplicationToUI(apiData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: applicationsKeys.lists() });
    },
  });
}

export function useUpdateApplication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      applicationId,
      application,
    }: {
      applicationId: string;
      application: Partial<Application>;
    }) => {
      const updateData = mapApplicationToUpdate(application);
      const apiData = await applicationsApi.updateApplication(applicationId, updateData);
      return mapApplicationShortToUI(apiData);
    },
    onMutate: async ({ applicationId }) => {
      await queryClient.cancelQueries({ queryKey: applicationsKeys.detail(applicationId) });

      const previousApplicationData = queryClient.getQueryData<ApplicationOutputSchema>(
        applicationsKeys.detail(applicationId)
      );

      return { previousApplicationData };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousApplicationData) {
        queryClient.setQueryData(
          applicationsKeys.detail(_variables.applicationId),
          context.previousApplicationData
        );
      }
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: applicationsKeys.detail(variables.applicationId),
      });
      queryClient.invalidateQueries({ queryKey: applicationsKeys.lists() });
    },
  });
}

export function useUpdateApplicationConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      applicationId,
      data,
    }: {
      applicationId: string;
      data: ApplicationConfigUpdateSchema;
    }) => {
      const apiData = await applicationsApi.updateApplicationConfig(applicationId, data);
      return apiData;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(applicationsKeys.detail(variables.applicationId), data);
      queryClient.invalidateQueries({ queryKey: applicationsKeys.lists() });
    },
  });
}

export function useAddOffer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      applicationId,
      data,
    }: {
      applicationId: string;
      data: ApplicationOfferConfigInputSchema;
    }) => {
      const apiData = await applicationsApi.addOffer(applicationId, data);
      return apiData;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(applicationsKeys.detail(variables.applicationId), data);
      queryClient.invalidateQueries({ queryKey: applicationsKeys.lists() });
    },
  });
}
